import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import {
  CalendarDays, MapPin, ExternalLink, Hotel, FileText, ArrowLeft, X, Star, GraduationCap, Download,
  Link as LinkIcon,
} from "lucide-react";

const ORIGIN = API.replace(/\/api$/, "");
const mediaUrl = (u) => (!u ? "" : (u.startsWith("http") ? u : `${ORIGIN}${u}`));
const ytEmbed = (url) => {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : "";
};

function ShareBtn({ href, label, color, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="flex items-center gap-1.5 text-xs text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
      style={{ backgroundColor: color }}>
      {label}
    </a>
  );
}

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};
const fmtRange = (a, b) => (b && b !== a ? `${fmtDate(a)} → ${fmtDate(b)}` : fmtDate(a));

const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d + "T00:00:00");
  return Math.round((target - today) / 86400000);
};

function CountdownTimer({ date, isBBQ }) {
  const target = date ? new Date(date + (isBBQ ? "T15:00:00" : "T00:00:00")).getTime() : null;
  const calc = () => {
    if (!target) return null;
    const diff = target - Date.now();
    if (diff <= 0) return { over: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc());
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [date]); // eslint-disable-line
  if (!t) return null;
  if (t.over) return <span className="text-sm font-bold text-emerald-400">C'est aujourd'hui ou déjà commencé ! 🎉</span>;
  const Cell = ({ v, l }) => (
    <div className="flex flex-col items-center bg-white/10 rounded-lg px-3 py-2 min-w-[56px]">
      <span className="text-2xl sm:text-3xl font-black tabular-nums leading-none">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] uppercase tracking-wider text-white/50 mt-1">{l}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2" data-testid="event-countdown">
      <Cell v={t.d} l="jours" /><Cell v={t.h} l="h" /><Cell v={t.m} l="min" /><Cell v={t.s} l="sec" />
    </div>
  );
}

export default function EvenementsPublic() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [participate, setParticipate] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/dojo-events`);
      setEvents(r.data.events || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="evenements-public">
      <header className="flex items-center justify-between px-6 lg:px-16 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D90429] flex items-center justify-center font-black">K</div>
          <span className="text-xs tracking-[0.3em] text-white/60">KAIZEN SYSTEM</span>
        </div>
        <a href="/" className="text-xs tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-2"><ArrowLeft className="w-3.5 h-3.5" /> RETOUR</a>
      </header>

      <div className="px-6 lg:px-16 py-12 max-w-6xl mx-auto">
        <p className="text-[#D90429] text-xs tracking-[0.3em] mb-3">INSTITUT IMPACT ÉLITE MARTIAL</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-3">Événements</h1>
        <p className="text-white/50 mb-10 max-w-2xl">Compétitions, séminaires et stages. Participe avec le dojo — indique-nous ton intérêt et on t'accompagne dans l'organisation.</p>

        {events.length === 0 ? (
          <p className="text-white/40" data-testid="public-events-empty">Aucun événement à venir pour le moment. Reviens bientôt !</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="public-events-grid">
            {events.map((e) => (
              <button key={e.id} onClick={() => setSelected(e)} data-testid={`public-event-${e.id}`}
                className="text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#D90429]/60 transition-colors overflow-hidden group">
                <div className="h-36 bg-gradient-to-br from-white/5 to-transparent relative flex items-center justify-center">
                  {e.poster_image ? <img src={mediaUrl(e.poster_image)} alt="" className="w-full h-full object-cover" /> : <CalendarDays className="w-10 h-10 text-white/20" />}
                  {e.featured_event && <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-bold bg-[#D90429] px-2 py-0.5 rounded-full"><Star className="w-3 h-3" /> Vedette</span>}
                  {(() => { const j = daysUntil(e.start_date); return (j !== null && j >= 0 && j <= 120) ? (
                    <span data-testid={`event-jbadge-${e.id}`} className="absolute top-3 right-3 text-[11px] font-black bg-blue-600 text-white px-2.5 py-1 rounded-full shadow-lg">
                      {j === 0 ? "Aujourd'hui !" : `J-${j}`}
                    </span>) : null; })()}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {e.discipline && <span className="text-[10px] text-white/40 uppercase tracking-wide">{e.discipline}</span>}
                    {e.instructor_teaching && <span className="text-[10px] font-bold text-amber-300 flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Kru enseigne</span>}
                  </div>
                  <h3 className="text-xl font-bold group-hover:text-[#D90429] transition-colors">{e.event_title}</h3>
                  <div className="mt-2 text-sm text-white/50 flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtRange(e.start_date, e.end_date)}</div>
                  {e.city && <div className="mt-1 text-sm text-white/40 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {e.city}</div>}
                  {e.short_description && <p className="mt-3 text-sm text-white/40 line-clamp-2">{e.short_description}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto p-4 sm:p-8" onClick={() => setSelected(null)} data-testid="public-event-detail">
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl mx-auto bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
            {selected.poster_image && <img src={mediaUrl(selected.poster_image)} alt="" className="w-full h-52 object-cover" />}
            <div className="p-6 sm:p-8 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[#D90429] text-xs tracking-[0.25em] mb-1">{(selected.discipline || "ÉVÉNEMENT").toUpperCase()}</p>
                  <h2 className="text-3xl font-black">{selected.event_title}</h2>
                  <div className="mt-2 text-white/60 flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtRange(selected.start_date, selected.end_date)}</span>
                    {selected.city && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {selected.venue_name || selected.city}</span>}
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white"><X className="w-6 h-6" /></button>
              </div>

              {selected.long_description && <p className="text-white/70 leading-relaxed whitespace-pre-line">{selected.long_description}</p>}

              {(() => { const j = daysUntil(selected.start_date); return (j !== null && j >= 0) ? (
                <div className="rounded-xl border border-blue-500/40 bg-gradient-to-r from-[#0b1e4d] to-[#123a8f]/50 px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200 mb-2">⏳ Compte à rebours avant l'événement</p>
                  <CountdownTimer date={selected.start_date} isBBQ={(selected.tags || []).includes("potluck") || /bbq/i.test(selected.event_title || "")} />
                </div>) : null; })()}

              {((selected.tags || []).includes("potluck") || /bbq/i.test(selected.event_title || "")) && (
                <div data-testid="bbq-urgency" className="flex items-center gap-3 rounded-xl border border-[#D90429]/50 bg-[#D90429]/10 px-4 py-3 animate-pulse">
                  <span className="text-2xl">🔥</span>
                  <div>
                    <p className="text-[#ff6b6b] font-bold text-sm uppercase tracking-wide">Places limitées — réserve vite !</p>
                    <p className="text-white/70 text-xs">Les inscriptions ferment quand le nombre de places est atteint. Confirme ta présence dès maintenant pour ne pas manquer le BBQ familial.</p>
                  </div>
                </div>
              )}

              {selected.public_notes && <div className="text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-sm">ℹ️ {selected.public_notes}</div>}

              <div className="flex items-center gap-3 flex-wrap">
                {selected.official_registration_url && <PubLink href={selected.official_registration_url} icon={ExternalLink} label="Site officiel" />}
                {selected.hotel_url && <PubLink href={selected.hotel_url} icon={Hotel} label="Réserver l'hôtel" />}
                {selected.schedule_url && <PubLink href={selected.schedule_url} icon={FileText} label="Horaire" />}
                <button onClick={() => setParticipate(selected)} data-testid="public-participate-btn"
                  className="flex items-center gap-2 bg-[#D90429] hover:bg-[#BF0424] text-white font-bold px-5 py-2.5 rounded-xl">
                  {((selected.tags || []).includes("potluck") || /bbq/i.test(selected.event_title || "")) ? "🍔 Je m'inscris au BBQ" : "Je participe avec le dojo"}
                </button>
                <a href={`${API}/public/dojo-events/${selected.id}/poster`} download data-testid="public-poster-btn"
                  className="flex items-center gap-2 border border-white/20 hover:border-white/50 text-white/80 px-4 py-2.5 rounded-xl text-sm">
                  <Download className="w-4 h-4" /> Télécharger l'affiche
                </a>
              </div>

              {ytEmbed(selected.highlights_url) && (
                <div data-testid="public-event-highlights">
                  <p className="text-white/50 text-xs tracking-[0.2em] mb-3">HIGHLIGHTS</p>
                  <div className="relative w-full rounded-xl overflow-hidden border border-white/10" style={{ paddingBottom: "56.25%" }}>
                    <iframe src={ytEmbed(selected.highlights_url)} title="Highlights" className="absolute inset-0 w-full h-full"
                      frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                </div>
              )}


              {/* Partage 1-clic */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="public-share-row">
                <span className="text-white/40 text-xs tracking-[0.2em] mr-1">PARTAGER</span>
                <ShareBtn testid="share-facebook" label="Facebook" color="#1877F2"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ORIGIN + "/evenements")}`} />
                <ShareBtn testid="share-whatsapp" label="WhatsApp" color="#25D366"
                  href={`https://wa.me/?text=${encodeURIComponent(selected.event_title + " — " + ORIGIN + "/evenements")}`} />
                <ShareBtn testid="share-x" label="X" color="#000000"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(selected.event_title)}&url=${encodeURIComponent(ORIGIN + "/evenements")}`} />
                <button data-testid="share-copy" onClick={() => { navigator.clipboard?.writeText(ORIGIN + "/evenements"); toast.success("Lien copié !"); }}
                  className="flex items-center gap-1.5 text-xs border border-white/20 hover:border-white/50 text-white/70 px-3 py-1.5 rounded-lg">
                  <LinkIcon className="w-3.5 h-3.5" /> Copier le lien
                </button>
              </div>

              {(selected.photos && selected.photos.length > 0) && (
                <div data-testid="public-event-gallery">
                  <p className="text-white/50 text-xs tracking-[0.2em] mb-3">GALERIE</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selected.photos.map((ph) => (
                      <a key={ph.id} href={`${ORIGIN}${ph.url}`} target="_blank" rel="noreferrer"
                        className="block rounded-lg overflow-hidden border border-white/10 hover:border-white/40">
                        <div className="aspect-square"><img src={`${ORIGIN}${ph.url}`} alt={ph.caption || ""} className="w-full h-full object-cover" loading="lazy" /></div>
                        {ph.caption && <div className="px-2 py-1 text-[11px] text-white/60 truncate">{ph.caption}</div>}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {participate && <ParticipateForm event={participate} onClose={() => setParticipate(null)} />}
    </div>
  );
}

function PubLink({ href, icon: Icon, label }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/20 hover:border-white/50 text-white/80 px-4 py-2.5 rounded-xl text-sm">
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}

function ParticipateForm({ event, onClose }) {
  const isBBQ = (event.tags || []).includes("potluck") || /bbq/i.test(event.event_title || "");
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "member", student_name: "", discipline: event.discipline || "",
    age_group: "", wants_to_attend: true, already_registered: isBBQ, already_paid: false, hotel_booked: false,
    transportation_needed: false, wants_shared_room: false, wants_carpool: false,
    party_size: 1, potluck_dish: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Ton nom est requis"); return; }
    setBusy(true);
    try {
      const payload = isBBQ ? { ...form, already_registered: true, wants_to_attend: true } : form;
      const r = await axios.post(`${API}/public/dojo-events/${event.id}/participate`, payload);
      setDone(true);
      toast.success(r.data.message || "Participation enregistrée !");
    } catch { toast.error("Erreur, réessaie."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="public-participate-form">
      <div onClick={(e) => e.stopPropagation()} className="max-w-lg mx-auto bg-[#111] border border-white/10 rounded-2xl p-6 sm:p-8">
        {done ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">{isBBQ ? "🍔" : "🥋"}</div>
            <h3 className="text-2xl font-black mb-2">Merci !</h3>
            <p className="text-white/60">{isBBQ
              ? `Ton inscription au « ${event.event_title} » est enregistrée. On a hâte de te voir avec ta famille !`
              : `Ta participation à « ${event.event_title} » est enregistrée. Le dojo te recontactera pour l'organisation.`}</p>
            <button onClick={onClose} className="mt-6 bg-[#D90429] hover:bg-[#BF0424] text-white font-bold px-6 py-2.5 rounded-xl">Fermer</button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[#D90429] text-xs tracking-[0.25em]">{isBBQ ? "JE M'INSCRIS AU BBQ" : "JE PARTICIPE AVEC LE DOJO"}</p>
                <h3 className="text-xl font-black">{event.event_title}</h3>
              </div>
              <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PubField className="sm:col-span-2" label="Nom complet *" value={form.full_name} onChange={(v) => set("full_name", v)} testid="pf-name" />
              <PubField label="Courriel" value={form.email} onChange={(v) => set("email", v)} testid="pf-email" />
              <PubField label="Téléphone" value={form.phone} onChange={(v) => set("phone", v)} testid="pf-phone" />
              {isBBQ ? (
                <>
                  <label className="block">
                    <span className="text-xs text-white/50">Nombre de personnes *</span>
                    <input type="number" min="1" value={form.party_size} onChange={(e) => set("party_size", Math.max(1, parseInt(e.target.value) || 1))}
                      data-testid="pf-party-size" className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                  </label>
                  <PubSel label="Je suis…" value={form.role} onChange={(v) => set("role", v)} testid="pf-role" options={[
                    { v: "member", l: "Membre" }, { v: "parent", l: "Parent" }, { v: "coach", l: "Coach" }, { v: "professor", l: "Autre" }]} />
                  <PubField className="sm:col-span-2" label="Plat apporté (potluck) 🍽️" value={form.potluck_dish} onChange={(v) => set("potluck_dish", v)} testid="pf-potluck-dish" />
                  <label className="sm:col-span-2 block">
                    <span className="text-xs text-white/50">Notes / allergies / questions</span>
                    <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pf-notes"
                      className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                  </label>
                  <p className="sm:col-span-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    🍔 Formule potluck : apportez un plat à partager. 25 $ par personne de 12 ans et +, GRATUIT pour les moins de 12 ans.
                  </p>
                </>
              ) : (
                <>
                  <PubSel label="Je suis…" value={form.role} onChange={(v) => set("role", v)} testid="pf-role" options={[
                    { v: "member", l: "Membre" }, { v: "parent", l: "Parent" }, { v: "professor", l: "Professeur" }, { v: "coach", l: "Coach" }]} />
                  <PubField label="Discipline" value={form.discipline} onChange={(v) => set("discipline", v)} testid="pf-disc" />
                  <PubField className="sm:col-span-2" label="Nom de l'élève (si parent)" value={form.student_name} onChange={(v) => set("student_name", v)} testid="pf-student" />
                  <label className="sm:col-span-2 block">
                    <span className="text-xs text-white/50">Notes / questions</span>
                    <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pf-notes"
                      className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                  </label>
                  <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                    <PubCheck label="Déjà inscrit (site officiel)" checked={form.already_registered} onChange={(v) => set("already_registered", v)} testid="pf-registered" />
                    <PubCheck label="Je veux une chambre partagée" checked={form.wants_shared_room} onChange={(v) => set("wants_shared_room", v)} testid="pf-shared" />
                    <PubCheck label="Transport à prévoir" checked={form.transportation_needed} onChange={(v) => set("transportation_needed", v)} testid="pf-transport" />
                    <PubCheck label="Je veux du covoiturage" checked={form.wants_carpool} onChange={(v) => set("wants_carpool", v)} testid="pf-carpool" />
                  </div>
                </>
              )}
            </div>
            <button onClick={submit} disabled={busy} data-testid="pf-submit"
              className="mt-5 w-full bg-[#D90429] hover:bg-[#BF0424] text-white font-bold py-3 rounded-xl disabled:opacity-50">
              {busy ? "Envoi…" : (isBBQ ? "Je m'inscris au BBQ" : "Envoyer ma participation")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const PubField = ({ label, value, onChange, testid, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs text-white/50">{label}</span>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm" />
  </label>
);
const PubSel = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-xs text-white/50">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm">
      {options.map((o) => <option key={o.v} value={o.v} className="bg-[#111]">{o.l}</option>)}
    </select>
  </label>
);
const PubCheck = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="accent-[#D90429] w-4 h-4" />
    {label}
  </label>
);
